/**
 * /admin/tenants/:id — per-tenant detail.
 *
 * Loads `/v1/admin/tenants/:id` and shows tenant metadata + recent
 * telemetry snapshots. Read-only; pause/unpause/migrate actions land
 * with the orchestrator (separate ticket).
 *
 * Telemetry empty-state spells out the prereq (cron-driven beacon
 * poll) — same approach as the list page's empty-state.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { DripLogPanel } from '../components/DripLogPanel';
import { ManagedAiPanel, type ManagedAiInfo } from '../components/ManagedAiPanel';
import { InstanceMailPanel } from '../components/InstanceMailPanel';
import { ManagedBackupPanel, type ManagedBackupInfo } from '../components/ManagedBackupPanel';
import { PinningPanel } from '../components/PinningPanel';
import { CodeGrantPanel, lockText, type WorkspaceLock } from '../components/CodeGrantPanel';
import { SelfHostSecretModal, type SelfHostSecretReveal } from '../components/SelfHostSecretModal';
import { api, ApiError } from '../lib/api';
import { OperatorMailForm } from '../components/OperatorMailForm';
import { AgentLanesField } from '../components/AgentLanesField';
import type { AgentLanesView } from '@orboto/ocp-shared';
import { fmtDateTime, fmtDate } from '../lib/format-time';

interface TenantDetailResponse {
  tenant: {
    id: string;
    subdomain: string;
    custom_domain: string | null;
    region: string;
    coolify_app_uuid: string | null;
    vps_server_id: string | null;
    status: string;
    subscription_state: string;
    is_self_host: boolean;
    self_host_url: string | null;
    license_expires_at: string | null;
    pool_tag: string;
    storage_quota_gb: number;
    /**
     * OCP-484 / OCP-142: deployed image tag = the running version (NULL
     * follows the rolling `latest` channel). `update_channel` is `latest`
     * or `pinned`; shown read-only here (the pin CONTROL is OCP-485).
     */
    image_tag: string | null;
    update_channel: 'latest' | 'pinned';
    origin: string;
    origin_id: string | null;
    s3_access_key: string | null;
    contact_email: string | null;
    contact_name: string | null;
    created_at: string;
    updated_at: string;
    paused_at: string | null;
    deleted_at: string | null;
    /** OCP-613 - null | purging | hard_deleted. */
    deletion_state?: string | null;
  /** OCP-593 - communication locale for operator mails. */
  locale?: string;
  /** OCP-663 / OCP-922 - `scratch` = operator throwaway, due for hard-delete at `scratch_expires_at`. */
  purpose?: string | null;
  scratch_expires_at?: string | null;
    archived_at?: string | null;
  };
  /**
   * OCP-613 - present on a hard-deleted / purging tenant: the live blocks
   * (managed_ai, managed_backup, seats, activity) are null then and this
   * block carries the facts. OCP-850 - no archive; the deletion record is
   * the evidence (AVV section 7 (2)).
   */
  deleted?: {
    state: 'hard_deleted' | 'purging';
    at: string | null;
    note: string;
    /** OCP-571 - post-erasure check of the managed-backup prefix (hard_deleted only). */
    backups?: { erased: boolean | null; archives_remaining: number | null; bytes_remaining: number | null; error: string | null } | null;
    /** OCP-850 - summary of the deletion record (hard_deleted only; null = none, e.g. not wired). */
    deletion_record?: DeletionRecordSummary | null;
  } | null;
  /**
   * OCP-458: Cloudflare zone tenants live on (`tenant.zone`, e.g.
   * `orbo.to`) - the instance URL is `<subdomain>.<zone>`, NEVER
   * `orboto.io`. Sourced from the API so the operator-configurable
   * setting is the single source of truth.
   */
  zone: string;
  /** OCP-917 - core's subscription-state lock (null for demo / self-host / deleted). */
  workspace_lock?: WorkspaceLock | null;
  /**
   * OCP-440: the resolved per-slice postgres tuning + connection budget
   * injected into the tenant compose. Reflects the tenant's team-size
   * bucket + current worker.slice_profiles settings, so it shows what a
   * (re)deploy applies.
   */
  resource_profile?: ResourceProfile;
  /**
   * OCP-401: managed-AI wiring status for this tenant (gateway configured,
   * whether a virtual key is wired, non-secret model/dimensions/base URL).
   * The virtual key material itself is never included.
   */
  managed_ai?: ManagedAiInfo;
  /** OCP-610 - "is this tenant using orboto?" summary from the periodic beacons (OCP-629: 30-minute or hourly). */
  activity?: {
    last_active_at: string | null;
    last_active_signal: 'active_users' | 'api_requests' | 'content' | null;
    days_since_activity: number | null;
    active_days: number;
    total_users: number | null;
    content_now: { tickets: number | null; docs: number | null; comments: number | null };
    reason: 'no_beacons' | 'no_activity_in_window' | null;
  } | null;
  /**
   * OCP-569: purchased-vs-billable seat usage ("x/y seats"). `state`
   * distinguishes a real overage from "not yet measurable" (tenant not on
   * an emitting core version) and "no cap" (nothing purchased to compare).
   */
  seats?: {
    purchased: number | null;
    billableUsers: number | null;
    measuredAt: string | null;
    state: 'ok' | 'overage' | 'not_measurable' | 'no_cap' | 'excluded';
    alertedAt: string | null;
  } | null;
  /** OCP-985 - hosted agent lanes paid (included + booked) and where `included` comes from. Null on a hard-deleted tenant. */
  agent_lanes?: AgentLanesView | null;
  /**
   * OCP-529: where this tenant's managed backups land (central backup
   * storage, `<tenant-id>/` prefix in the shared bucket) + whether any
   * actually did. Null for self-host tenants (they run their own backups).
   */
  managed_backup?: ManagedBackupInfo | null;
  /**
   * OCP-643 / ORB-2032 - has the provisioned instance admin chosen the
   * instance password yet? The instance login is a separate account from the
   * customer portal; while pending, only the welcome magic link gets them in.
   * null = no beacon carried the flag yet (core < 0.188.1).
   */
  onboarding?: {
    admin_password_pending: boolean | null;
    observed_at: string | null;
    pending_since: string | null;
    pending_hours: number | null;
  } | null;
  latest_telemetry: TelemetryRow | null;
  recent_telemetry: TelemetryRow[];
  rollback:
    | { rollbackable: true; has_failed_events: boolean }
    | { rollbackable: false; reason: 'not_found' | 'already_active' | 'deleted' }
    | null;
  /** OCP-870 - single-flight deploy state of the stack (null = never deployed through the guard). */
  deploy_guard?: DeployGuardView | null;
}

/** OCP-870 - the `deploy_guard` block of the tenant detail. */
interface DeployGuardView {
  service_uuid: string;
  in_flight: boolean;
  in_flight_since: string | null;
  source: string | null;
  actor: string | null;
  target_tag: string | null;
  override_reason: string | null;
  expires_at: string | null;
  stale: boolean;
  coolify_transition_since: string | null;
  coolify_transition_status: string | null;
  last_outcome: string | null;
  last_outcome_detail: string | null;
  last_outcome_at: string | null;
}

/** OCP-870 - what a 409 deploy_in_flight carries. */
interface DeployInFlightInfo {
  kind: 'ocp' | 'coolify';
  source: string | null;
  actor: string | null;
  target_tag: string | null;
  since: string;
  age_seconds: number;
  expires_at: string;
  coolify_status: string | null;
}

/** OCP-850 - what the tenant detail carries of the deletion record. */
interface DeletionRecordSummary {
  id: string;
  deleted_at: string;
  trigger: 'export_window_elapsed' | 'operator';
  contract_ended_at: string | null;
  export_window_ends_at: string | null;
  export_window_extended_until?: string | null;
  resources: Array<{ resource: string; outcome: 'deleted' | 'none_present' | 'not_confirmed' }>;
  source: string;
  locale: 'de' | 'en';
  backfilled: boolean;
}

const DELETION_RESOURCE_LABEL: Record<string, string> = {
  coolify: 'Stack (database + volumes)',
  rustfs: 'File storage',
  cloudflare: 'DNS',
  litellm: 'AI key',
  oms_mail: 'Mail key',
  backups: 'Managed backups',
  file_backups: 'File backup snapshots',
  training_data: 'Training data',
  backup_key: 'Backup key',
  control_plane_record: 'Contact data + credentials on the row',
};

/**
 * OCP-850 - the AVV section 7 (2) deletion record: facts + per-resource
 * outcome, and the customer-ready PDF in both languages (the customer's
 * owner can download the same PDF in the portal).
 */
function DeletionRecordBlock({ tenantId, record }: { tenantId: string; record: DeletionRecordSummary | null }) {
  if (!record) {
    return (
      <p data-testid="deletion-record-missing" className="mt-2 text-xs text-red-700 dark:text-red-300">
        No deletion record found for this tenant.
      </p>
    );
  }
  const base = `/v1/admin/tenants/${tenantId}/deletion-record.pdf`;
  return (
    <div data-testid="deletion-record" className="mt-3 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-xs">
      <div className="font-semibold">
        Deletion record <span className="font-mono font-normal text-gray-500">{record.id}</span>
        {record.backfilled && <span className="ml-2 font-normal text-gray-500">(reconstructed from the audit log)</span>}
      </div>
      <dl className="mt-1 grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2">
        <div>
          <dt className="inline text-gray-500">Contract end: </dt>
          <dd className="inline">{record.contract_ended_at ? fmtDateTime(record.contract_ended_at) : 'n/a'}</dd>
        </div>
        <div>
          <dt className="inline text-gray-500">Export window ended: </dt>
          <dd className="inline">{record.export_window_ends_at ? fmtDateTime(record.export_window_ends_at) : 'n/a'}</dd>
        </div>
        {record.export_window_extended_until && (
          <div data-testid="deletion-record-extended">
            <dt className="inline text-gray-500">Export window extended until: </dt>
            <dd className="inline">{fmtDateTime(record.export_window_extended_until)}</dd>
          </div>
        )}
        <div>
          <dt className="inline text-gray-500">Deleted: </dt>
          <dd className="inline">{fmtDateTime(record.deleted_at)}</dd>
        </div>
        <div>
          <dt className="inline text-gray-500">Trigger: </dt>
          <dd className="inline">
            {record.trigger === 'export_window_elapsed' ? 'export window elapsed (sweep)' : 'operator'} ({record.source})
          </dd>
        </div>
      </dl>
      <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
        {record.resources.map((r) => (
          <li key={r.resource} data-testid={`deletion-resource-${r.resource}`}>
            {DELETION_RESOURCE_LABEL[r.resource] ?? r.resource}:{' '}
            <span className={r.outcome === 'not_confirmed' ? 'text-red-700 dark:text-red-300' : 'text-gray-600 dark:text-gray-400'}>
              {r.outcome === 'deleted' ? 'deleted' : r.outcome === 'none_present' ? 'none present' : 'not confirmed'}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex gap-3">
        <a data-testid="deletion-record-pdf-de" className="text-brand-700 underline" href={`${base}?lang=de`}>PDF (de)</a>
        <a data-testid="deletion-record-pdf-en" className="text-brand-700 underline" href={`${base}?lang=en`}>PDF (en)</a>
        <span className="text-gray-500">Account language: {record.locale}</span>
      </div>
    </div>
  );
}

interface ResourceProfile {
  profile_name: string;
  team_size_bucket: string | null;
  /** OCP-368 - slots this tenant counts against the host's max_tenants. */
  capacity_weight?: number;
  threads: number;
  ram_mb: number;
  api_workers: number;
  max_connections: number;
  env: {
    POSTGRES_MAX_CONNECTIONS: string;
    PG_SHARED_BUFFERS: string;
    PG_EFFECTIVE_CACHE_SIZE: string;
    PG_WORK_MEM: string;
    PG_MAINTENANCE_WORK_MEM: string;
    PG_RANDOM_PAGE_COST: string;
    API_WORKERS: string;
    DB_POOL_MAX: string;
  };
}

interface TelemetryRow {
  id: string;
  tenant_id: string;
  snapshot_at: string;
  period_minutes: number;
  active_users_24h: number | null;
  api_requests_count: number | null;
  ai_tokens_in: number | null;
  ai_tokens_out: number | null;
  ai_calls_count: number | null;
  embeddings_count: number | null;
  ccu_peak: number | null;
  ccu_avg: number | null;
  storage_total_gb: number | null;
  ticket_count: number | null;
  doc_count: number | null;
  comment_count: number | null;
  cpu_pct_avg: number | null;
  cpu_pct_p95: number | null;
  ram_used_mb_avg: number | null;
  disk_used_gb: number | null;
}

export function AdminTenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<TenantDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setError(null);
    setNotFound(false);
    try {
      const r = await api<TenantDetailResponse>(`/v1/admin/tenants/${id}`);
      setData(r);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setNotFound(true);
      else if (err instanceof ApiError) setError(`Could not load tenant (HTTP ${err.status}).`);
      else setError('Could not load tenant.');
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl">
        <Link to="/tenants" className="text-sm text-brand-500 hover:text-brand-600">
          ← Back to tenants
        </Link>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight">Tenant not found</h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          No tenant matches this id. It may have been hard-deleted, or the link is stale.
        </p>
      </div>
    );
  }

  // OCP-458: the tenant instance host is `<subdomain>.<zone>` (or the
  // custom domain), where `zone` (e.g. `orbo.to`) comes from the API,
  // NOT a hardcoded `orboto.io`. Web-only public URL is https-only.
  // OCP-444: a self-host tenant serves at its external `self_host_url`, never
  // at `<subdomain>.<zone>`.
  const selfHostUrl = data?.tenant.is_self_host ? data.tenant.self_host_url : null;
  const instanceHref = data
    ? selfHostUrl ??
      `https://${data.tenant.custom_domain ?? `${data.tenant.subdomain}.${data.zone}`}`
    : null;
  const instanceHost = instanceHref
    ? instanceHref.replace(/^https?:\/\//, '').replace(/\/$/, '')
    : null;

  return (
    <div className="mx-auto max-w-4xl">
      <Link to="/tenants" className="text-sm text-brand-500 hover:text-brand-600">
        ← Back to tenants
      </Link>
      {error && (
        <div
          role="alert"
          className="mt-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}
      {data === null && !error ? (
        <div className="mt-6 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
          Loading…
        </div>
      ) : data ? (
        <>
          <div className="mt-2 flex items-baseline justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {data.tenant.subdomain}
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                <a
                  href={instanceHref ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-500 hover:text-brand-600 hover:underline"
                >
                  {instanceHost}
                </a>{' '}
                · {data.tenant.region} · {data.tenant.pool_tag}
                {data.tenant.is_self_host && (
                  <span className="ml-2 inline-flex items-center rounded bg-sky-50 dark:bg-sky-900/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300 ring-1 ring-inset ring-sky-200">
                    self-host
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* OCP-613 - a dead tenant is announced before anything that reads "active". */}
          {data.deleted && (
            <div
              role="status"
              data-testid="deletion-banner"
              className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
                data.deleted.state === 'hard_deleted'
                  ? 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-gray-800 dark:text-gray-200'
                  : 'border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200'
              }`}
            >
              <div className="font-semibold">
                {data.deleted.state === 'hard_deleted' ? 'Hard-deleted' : 'Purging'}
                {data.deleted.at && <span className="ml-2 font-normal text-gray-500">{fmtDateTime(data.deleted.at)}</span>}
              </div>
              <p className="mt-1">{data.deleted.note}</p>
              {data.deleted.state === 'hard_deleted' && (
                <DeletionRecordBlock tenantId={data.tenant.id} record={data.deleted.deletion_record ?? null} />
              )}
              {data.deleted.backups && (
                <p data-testid="deletion-backups" className="mt-1 text-xs">
                  {data.deleted.backups.erased === true && 'Managed-backup archives: erased (prefix empty).'}
                  {data.deleted.backups.erased === false &&
                    `Managed-backup archives: ${data.deleted.backups.archives_remaining} still present (${Math.round((data.deleted.backups.bytes_remaining ?? 0) / 1024 / 1024)} MB) - purge via the backups purge endpoint (OCP-602).`}
                  {data.deleted.backups.erased === null && `Managed-backup archives: could not verify - ${data.deleted.backups.error ?? 'unknown'}.`}
                </p>
              )}
            </div>
          )}

          <section className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Status" value={data.deleted ? data.deleted.state : data.tenant.status} />
            <LocaleField tenantId={data.tenant.id} locale={data.tenant.locale ?? 'en'} disabled={!!data.deleted} onSaved={load} />
            {data.tenant.purpose === 'scratch' && data.tenant.origin !== 'restore_drill' && !data.deleted && (
              <ScratchExpiryField tenantId={data.tenant.id} expiresAt={data.tenant.scratch_expires_at ?? null} onSaved={load} />
            )}
            <Field label="Subscription" value={data.tenant.subscription_state} />
            {data.workspace_lock && (
              <Field
                label="Workspace lock"
                value={lockText(data.workspace_lock)}
                hint="A paused workspace runs read-only with the closed page (SUBSCRIPTION_STATE=cancelled); the 5-minute lock sweep delivers changes."
              />
            )}
            <Field
              label="License expires"
              value={
                data.tenant.license_expires_at
                  ? fmtDate(data.tenant.license_expires_at)
                  : '—'
              }
            />
            <Field label="Storage quota (GB)" value={String(data.tenant.storage_quota_gb)} />
            {/* OCP-610 - last activity for the support call. */}
            <Field
              label="Last active"
              value={
                !data.activity
                  ? '—'
                  : data.activity.reason === 'no_beacons'
                    ? 'no beacons yet'
                    : data.activity.last_active_at
                      ? `${fmtDate(data.activity.last_active_at)} (${data.activity.days_since_activity} d ago, ${data.activity.active_days} active day${data.activity.active_days === 1 ? '' : 's'} / 30)`
                      : 'no activity in 30 days'
              }
            />
            {/* OCP-630 - this month's availability against the SLA commitment. */}
            {!data.deleted && !data.tenant.is_self_host && <AvailabilityField tenantId={data.tenant.id} />}
            {/* OCP-643 - instance admin password state from the beacon (separate account from the portal). */}
            {data.onboarding && (
              <Field
                label="Instance admin password"
                value={
                  data.onboarding.admin_password_pending === null
                    ? 'not reported yet (core < 0.188.1)'
                    : data.onboarding.admin_password_pending
                      ? `not set - welcome link pending${data.onboarding.pending_since ? ` since ${fmtDate(data.onboarding.pending_since)} (${data.onboarding.pending_hours ?? 0} h)` : ''}; the customer cannot log in with a password yet`
                      : 'set (instance login works; separate from the portal password)'
                }
              />
            )}
            {/* OCP-643 - re-send / rotate the first-boot setup link while the password is pending. */}
            {data.onboarding && data.onboarding.admin_password_pending !== false && !data.deleted && !data.tenant.is_self_host && data.tenant.status === 'active' && (
              <SetupLinkField tenantId={data.tenant.id} onSent={load} />
            )}
            {/* OCP-649 - one-off operator mail to the tenant contact. */}
            {!data.deleted && (
              <OperatorMailForm target={{ tenant_id: data.tenant.id }} />
            )}
            {/* OCP-569 - "x/y seats" with the honest not-measurable state. */}
            {data.seats && data.seats.state !== 'excluded' && (
              <Field
                label="Seats"
                value={
                  data.seats.state === 'no_cap'
                    ? 'no seat cap'
                    : data.seats.state === 'not_measurable'
                      ? `${data.seats.purchased ?? '?'} purchased - usage not yet measurable`
                      : `${data.seats.billableUsers}/${data.seats.purchased} seats${
                          data.seats.state === 'overage' ? ' - OVER' : ''
                        }`
                }
                hint={
                  data.seats.measuredAt
                    ? `billable users as of ${fmtDateTime(data.seats.measuredAt)}`
                    : 'core has not reported billable_users yet (needs >= 0.168.3 + telemetry on)'
                }
              />
            )}
            {/* OCP-985 - agent lanes paid, with the audited edit dialog (same route as ocp_set_agent_lanes). */}
            {data.agent_lanes && (
              <AgentLanesField
                target={{ kind: 'tenant', id: data.tenant.id }}
                lanes={data.agent_lanes}
                editable={!data.deleted && !data.tenant.is_self_host}
                onChanged={() => void load()}
              />
            )}
            <Field label="Origin" value={data.tenant.origin} />
            <Field
              label="Created"
              value={fmtDate(data.tenant.created_at)}
            />
            <Field
              label="Contact"
              value={data.tenant.contact_email ?? '—'}
              hint={data.tenant.contact_name ?? undefined}
            />
            <Field
              label="Coolify app"
              value={
                data.tenant.coolify_app_uuid
                  ? data.tenant.coolify_app_uuid.slice(0, 8) + '…'
                  : '—'
              }
            />
            <Field
              label="S3 access key"
              value={data.tenant.s3_access_key ?? '—'}
            />
          </section>

          {!data.tenant.is_self_host && (
            <VersionLifecyclePanel
              tenantId={data.tenant.id}
              subdomain={data.tenant.subdomain}
              imageTag={data.tenant.image_tag}
              updateChannel={data.tenant.update_channel}
              storageQuotaGb={data.tenant.storage_quota_gb}
              provisioned={data.tenant.coolify_app_uuid !== null}
              deployGuard={data.deploy_guard ?? null}
              onChanged={() => void load()}
            />
          )}

          {data.resource_profile && (
            <section className="mt-10">
              <h2 className="mb-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                Resource profile
              </h2>
              <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                Per-slice Postgres tuning + connection budget injected into the tenant compose.
                Resolved from the plan bucket + current settings; applied on the next (re)deploy.
              </p>
              <ResourceProfileSection profile={data.resource_profile} />
              {!data.tenant.is_self_host && <TeamSizeEditor tenantId={data.tenant.id} profile={data.resource_profile} onChanged={() => void load()} />}
            </section>
          )}

          {data.tenant.is_self_host && (
            <SelfHostPanel
              tenantId={data.tenant.id}
              subdomain={data.tenant.subdomain}
              selfHostUrl={data.tenant.self_host_url}
            />
          )}

          {/* OCP-485: per-tenant version pin/unpin. Self-host tenants run no
              Coolify service of ours, so pinning is meaningless for them. */}
          {/* OCP-911 - grant end, grace, reminder stages, extend (renders nothing for a non-code tenant). */}
          {!data.deleted && (data.tenant.origin === 'invite_code' || data.tenant.origin === 'prepaid_code') && <CodeGrantPanel tenantId={data.tenant.id} />}
          {!data.tenant.is_self_host && <PinningPanel tenantId={data.tenant.id} />}

          {/* OCP-401: managed-AI wiring status + key rotate. Self-host tenants
              run their own AI config, so this is pool-tenant only. */}
          {!data.tenant.is_self_host && data.managed_ai && (
            <ManagedAiPanel
              tenantId={data.tenant.id}
              info={data.managed_ai}
              provisioned={data.tenant.coolify_app_uuid !== null}
              onChanged={() => void load()}
            />
          )}

          {/* OCP-591: tenant-included instance mail - wiring state + the
              wire/backfill action. Self-host tenants bring their own mail
              provider (ORB-896 / OCP-586 path), so this is pool-tenant only. */}
          {!data.tenant.is_self_host && (
            <InstanceMailPanel
              tenantId={data.tenant.id}
              provisioned={data.tenant.coolify_app_uuid !== null}
            />
          )}

          {/* OCP-529: managed-backup target + this tenant's latest archive.
              Self-host tenants back themselves up, so the API sends null. */}
          {data.managed_backup && <ManagedBackupPanel info={data.managed_backup} tenantId={data.tenant.id} />}

          <section className="mt-10">
            <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">Telemetry</h2>
            <TelemetrySection
              latest={data.latest_telemetry}
              recent={data.recent_telemetry}
            />
          </section>

          {/* OCP-444: self-host tenants get no onboarding drip - hide the panel. */}
          {!data.tenant.is_self_host && (
            <section className="mt-10">
              <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100">Onboarding drip</h2>
              <DripLogPanel tenantId={data.tenant.id} />
            </section>
          )}

          {data.rollback?.rollbackable === true && (
            <RollbackPanel
              tenantId={data.tenant.id}
              subdomain={data.tenant.subdomain}
              hasFailedEvents={data.rollback.has_failed_events}
            />
          )}
        </>
      ) : null}
    </div>
  );
}

// ---- VersionLifecyclePanel (OCP-484) --------------------------------
//
// Surfaces the tenant's running version + update channel (read-only), and
// the two per-tenant lifecycle controls that were missing from this page:
//   - "Update now" -> POST /:id/redeploy (redeploys on the tenant's channel)
//   - quota edit    -> PATCH /:id/quota  (propagates STORAGE_QUOTA_GB to the
//                      running instance + redeploys, then flips the DB row)
// Both redeploy the tenant, so each sits behind an inline confirm. Version
// PINNING (choosing a target tag / editing the channel) is OCP-485 - the
// channel is DISPLAYED here, never edited.

function VersionLifecyclePanel({
  tenantId,
  subdomain,
  imageTag,
  updateChannel,
  storageQuotaGb,
  provisioned,
  deployGuard,
  onChanged,
}: {
  tenantId: string;
  subdomain: string;
  imageTag: string | null;
  updateChannel: 'latest' | 'pinned';
  storageQuotaGb: number;
  provisioned: boolean;
  /** OCP-870 - in-flight deploy + last outcome. */
  deployGuard: DeployGuardView | null;
  onChanged: () => void;
}) {
  const [quotaInput, setQuotaInput] = useState(String(storageQuotaGb));
  const [quotaConfirm, setQuotaConfirm] = useState(false);
  const [updateConfirm, setUpdateConfirm] = useState(false);
  const [busy, setBusy] = useState<null | 'quota' | 'update'>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // OCP-870 - a redeploy refused with deploy_in_flight offers the audited override.
  const [inFlight, setInFlight] = useState<DeployInFlightInfo | null>(null);
  const [overrideReason, setOverrideReason] = useState('');

  const parsedQuota = Number.parseInt(quotaInput, 10);
  const quotaValid =
    Number.isInteger(parsedQuota) && parsedQuota >= 1 && parsedQuota <= 100_000;
  const quotaChanged = quotaValid && parsedQuota !== storageQuotaGb;
  const runningVersion = imageTag ?? 'latest';

  const applyQuota = useCallback(async () => {
    if (!quotaChanged) return;
    setBusy('quota');
    setError(null);
    setNotice(null);
    try {
      await api(`/v1/admin/tenants/${tenantId}/quota`, {
        method: 'PATCH',
        body: { storage_quota_gb: parsedQuota },
      });
      setQuotaConfirm(false);
      setNotice(`Storage quota set to ${parsedQuota} GB - ${subdomain} is redeploying to apply it.`);
      onChanged();
    } catch (err) {
      setError(lifecycleErrorMessage(err, 'quota'));
    } finally {
      setBusy(null);
    }
  }, [quotaChanged, parsedQuota, tenantId, subdomain, onChanged]);

  const updateNow = useCallback(async (override?: { reason: string }) => {
    setBusy('update');
    setError(null);
    setNotice(null);
    try {
      const r = await api<{ image_tag?: string; guard_overridden?: boolean }>(`/v1/admin/tenants/${tenantId}/redeploy`, {
        method: 'POST',
        ...(override ? { body: { override: true, reason: override.reason } } : {}),
      });
      setUpdateConfirm(false);
      setInFlight(null);
      setOverrideReason('');
      setNotice(
        (r.image_tag
          ? `Redeploy triggered on ${r.image_tag} - status flips once Coolify finishes.`
          : 'Redeploy triggered - status flips once Coolify finishes.') +
          (r.guard_overridden ? ' The in-flight guard was overridden (audited).' : ''),
      );
      onChanged();
    } catch (err) {
      setInFlight(deployInFlightOf(err));
      setError(lifecycleErrorMessage(err, 'update'));
    } finally {
      setBusy(null);
    }
  }, [tenantId, onChanged]);

  return (
    <section className="mt-10 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        Version &amp; lifecycle
      </h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Running version is the deployed image tag. Update + quota changes redeploy the
        tenant, so each asks for a confirm.
      </p>

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">
        <div>
          <dt className="text-xs uppercase tracking-wide text-gray-500">Running version</dt>
          <dd className="mt-0.5 font-mono text-sm text-gray-900 dark:text-gray-100">
            {runningVersion}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-gray-500">Update channel</dt>
          <dd className="mt-0.5">
            <UpdateChannelPill channel={updateChannel} />
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wide text-gray-500">Storage quota</dt>
          <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{storageQuotaGb} GB</dd>
        </div>
      </dl>

      <DeployGuardStatus guard={deployGuard} />

      {error && (
        <div
          role="alert"
          className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}
      {inFlight && (
        <div className="mt-3 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-3">
          <p className="text-xs text-amber-900 dark:text-amber-200">
            Redeploy anyway only to recover a stack stuck mid-start (Coolify <code>starting:unknown</code>,
            containers in <code>Created</code>) - a second deploy on top of one that is still running is what
            leaves a stack in that state. The reason is recorded in the audit log.
          </p>
          <label htmlFor="deploy-override-reason" className="mt-2 block text-xs font-medium text-amber-900 dark:text-amber-200">
            Override reason
          </label>
          <textarea
            id="deploy-override-reason"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="e.g. stack stuck in starting:unknown after a concurrent deploy"
            className="mt-1 w-full rounded border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={() => void updateNow({ reason: overrideReason.trim() })}
            disabled={busy !== null || overrideReason.trim().length < 10}
            className="mt-2 rounded bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {busy === 'update' ? 'Redeploying…' : 'Redeploy anyway'}
          </button>
          {overrideReason.trim().length < 10 && (
            <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">At least 10 characters.</p>
          )}
        </div>
      )}
      {notice && (
        <div className="mt-4 rounded border border-emerald-300 dark:border-emerald-700 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300">
          {notice}
        </div>
      )}

      {!provisioned ? (
        <p className="mt-4 rounded border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-3 text-sm text-gray-500">
          No Coolify service yet - update + quota changes need a provisioned tenant.
        </p>
      ) : (
        <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Update now</h3>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Redeploys{' '}
              {updateChannel === 'pinned'
                ? `on the pinned tag (${runningVersion})`
                : 'on the rolling latest channel'}{' '}
              and re-applies the tenant env.
            </p>
            {updateConfirm ? (
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void updateNow()}
                  disabled={busy !== null}
                  className="rounded bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {busy === 'update' ? 'Redeploying…' : 'Confirm redeploy'}
                </button>
                <button
                  type="button"
                  onClick={() => setUpdateConfirm(false)}
                  disabled={busy !== null}
                  className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setUpdateConfirm(true);
                  setError(null);
                  setNotice(null);
                }}
                disabled={busy !== null}
                className="mt-3 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
              >
                Update now
              </button>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/30 p-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Storage quota</h3>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Sets STORAGE_QUOTA_GB on the running instance + redeploys to apply.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={100000}
                value={quotaInput}
                onChange={(e) => {
                  setQuotaInput(e.target.value);
                  setQuotaConfirm(false);
                }}
                aria-label="Storage quota in GB"
                className="w-28 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm tabular-nums focus:border-brand-500 focus:outline-none"
              />
              <span className="text-sm text-gray-500">GB</span>
            </div>
            {quotaValid && !quotaChanged && (
              <p className="mt-2 text-xs text-gray-500">Matches the current quota.</p>
            )}
            {!quotaValid && (
              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                Enter a whole number between 1 and 100000.
              </p>
            )}
            {quotaConfirm ? (
              <div className="mt-3 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void applyQuota()}
                  disabled={busy !== null || !quotaChanged}
                  className="rounded bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {busy === 'quota' ? 'Applying…' : `Confirm ${parsedQuota} GB`}
                </button>
                <button
                  type="button"
                  onClick={() => setQuotaConfirm(false)}
                  disabled={busy !== null}
                  className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setQuotaConfirm(true);
                  setError(null);
                  setNotice(null);
                }}
                disabled={busy !== null || !quotaChanged}
                className="mt-3 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Apply quota
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function UpdateChannelPill({ channel }: { channel: 'latest' | 'pinned' }) {
  if (channel === 'pinned') {
    return (
      <span className="inline-flex items-center rounded bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:text-amber-300 ring-1 ring-inset ring-amber-200 dark:ring-amber-800">
        pinned
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300 ring-1 ring-inset ring-gray-200 dark:ring-gray-700">
      latest
    </span>
  );
}

/** OCP-870 - the `in_flight` of a 409 deploy_in_flight, else null. */
function deployInFlightOf(err: unknown): DeployInFlightInfo | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { error?: string; in_flight?: DeployInFlightInfo } | null;
  return body?.error === 'deploy_in_flight' && body.in_flight ? body.in_flight : null;
}

/** OCP-870 - one line naming what is in flight. */
function describeInFlight(i: DeployInFlightInfo): string {
  const what = i.kind === 'ocp'
    ? `an OCP deploy (${i.source ?? 'unknown source'}${i.actor ? `, ${i.actor}` : ''}${i.target_tag ? `, target ${i.target_tag}` : ''})`
    : `Coolify reports the service as ${i.coolify_status ?? 'transitioning'} (a deploy outside OCP, or a stack stuck mid-start)`;
  return `${what} since ${fmtDateTime(i.since)}; the guard lets go at ${fmtDateTime(i.expires_at)}`;
}

/**
 * OCP-870 - the single-flight deploy state: a deploy in flight blocks the next
 * one (409 deploy_in_flight); a failed last deploy is the stuck-stack runbook.
 */
function DeployGuardStatus({ guard }: { guard: DeployGuardView | null }) {
  if (!guard) return null;
  const failed = guard.last_outcome === 'failed_after_retry' || guard.last_outcome === 'retry_failed';
  return (
    <div className="mt-4 space-y-2 text-sm" data-testid="deploy-guard">
      {guard.in_flight && guard.in_flight_since && (
        <p className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-amber-900 dark:text-amber-200">
          Deploy in flight since {fmtDateTime(guard.in_flight_since)} ({guard.source ?? 'unknown source'}
          {guard.actor ? `, ${guard.actor}` : ''}{guard.target_tag ? `, target ${guard.target_tag}` : ''}). Further deploys
          answer 409 until the after-deploy check confirms it{guard.expires_at ? ` or ${fmtDateTime(guard.expires_at)}` : ''}.
          {guard.override_reason ? ` Started with an override: ${guard.override_reason}` : ''}
        </p>
      )}
      {!guard.in_flight && guard.coolify_transition_since && (
        <p className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-amber-900 dark:text-amber-200">
          Coolify has reported the service as {guard.coolify_transition_status ?? 'transitioning'} since{' '}
          {fmtDateTime(guard.coolify_transition_since)} without an OCP deploy.
        </p>
      )}
      {guard.last_outcome && (
        <p className={failed ? 'rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-red-700 dark:text-red-300' : 'text-xs text-gray-600 dark:text-gray-400'}>
          Last deploy: <span className="font-mono">{guard.last_outcome}</span>
          {guard.last_outcome_at ? ` at ${fmtDateTime(guard.last_outcome_at)}` : ''}
          {guard.last_outcome_detail ? ` - ${guard.last_outcome_detail}` : ''}
          {failed ? ' Runbook: deploy/README.md "Runbook: tenant stack stuck after a deploy".' : ''}
        </p>
      )}
    </div>
  );
}

function lifecycleErrorMessage(err: unknown, kind: 'quota' | 'update'): string {
  const verb = kind === 'quota' ? 'update quota' : 'trigger update';
  if (err instanceof ApiError) {
    const code = (err.body as { error?: string } | null)?.error;
    const inFlight = deployInFlightOf(err);
    if (inFlight) {
      return `Not deployed: another deploy is in flight - ${describeInFlight(inFlight)}. Nothing was written.`;
    }
    if (code === 'not_provisioned') {
      return `Tenant has no Coolify service to ${kind === 'quota' ? 'propagate the quota to' : 'redeploy'}.`;
    }
    if (err.status === 404) return 'Tenant not found - it may have been rolled back.';
    return `Could not ${verb} (HTTP ${err.status}).`;
  }
  return `Could not ${verb}.`;
}

// ---- RollbackPanel (OCP-67) -----------------------------------

function RollbackPanel({
  tenantId,
  subdomain,
  hasFailedEvents,
}: {
  tenantId: string;
  subdomain: string;
  hasFailedEvents: boolean;
}) {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [queued, setQueued] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [pollResult, setPollResult] = useState<'cleaned' | 'still-here' | 'error' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const matches = confirmText.trim() === subdomain;

  // Poll /v1/admin/tenants/:id every 2s until we get a 404 (= cleaned)
  // or we've tried 30 times (= 60s, give up + show stuck warning).
  useEffect(() => {
    if (!queued || pollResult === 'cleaned') return;
    const timer = setInterval(async () => {
      try {
        await api(`/v1/admin/tenants/${tenantId}`);
        setPollAttempts((n) => n + 1);
        if (pollAttempts >= 30) {
          setPollResult('error');
          clearInterval(timer);
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          setPollResult('cleaned');
          clearInterval(timer);
        }
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [queued, pollAttempts, pollResult, tenantId]);

  const submit = useCallback(async () => {
    if (!matches) return;
    setSubmitting(true);
    setError(null);
    try {
      await api(`/v1/admin/tenants/${tenantId}/rollback`, {
        method: 'POST',
        body: { confirm_subdomain: subdomain },
      });
      setQueued(true);
    } catch (err) {
      if (err instanceof ApiError) {
        const code = (err.body as { error?: string } | null)?.error;
        if (code === 'tenant_active') {
          setError('Cannot rollback an active tenant. Use the hard-delete flow instead.');
        } else if (code === 'confirm_subdomain_mismatch') {
          setError('Subdomain confirmation did not match. Retry.');
        } else {
          setError(`Could not enqueue rollback (HTTP ${err.status}).`);
        }
      } else {
        setError('Could not enqueue rollback.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [matches, subdomain, tenantId]);

  return (
    <section className="mt-10 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 p-5">
      <h2 className="text-base font-semibold text-red-900 dark:text-red-200">Danger zone: roll back this tenant</h2>
      <p className="mt-2 text-sm text-red-800 dark:text-red-300">
        This deletes the Coolify service, the RustFS bucket + access key, the Cloudflare CNAME, and
        the tenant row + its lifecycle events + telemetry snapshots. The invite-code redemption row
        is preserved (audit trail), with its <code>tenant_id</code> nulled out.
      </p>
      {hasFailedEvents && (
        <p className="mt-2 text-sm font-medium text-red-900 dark:text-red-200">
          This tenant has one or more failed lifecycle events — typically why you&apos;re here.
        </p>
      )}
      <p className="mt-2 text-sm text-red-800 dark:text-red-300">
        <strong>Cannot be undone.</strong> Existing storage is irrecoverable after the bucket
        deletion. Make sure the tenant has no data worth keeping before clicking.
      </p>

      {queued ? (
        <div className="mt-4 rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 p-3 text-sm">
          {pollResult === 'cleaned' ? (
            <span className="font-medium text-green-700">
              ✓ Tenant rolled back. The page will keep showing &ldquo;not found&rdquo; on next reload.
            </span>
          ) : pollResult === 'error' ? (
            <span className="font-medium text-red-800 dark:text-red-300">
              Job is taking longer than expected ({pollAttempts * 2}s). The audit log shows what
              the worker did. Check the pg-boss job state via SQL if it stays stuck.
            </span>
          ) : (
            <span className="text-gray-700 dark:text-gray-300">
              Rollback job queued — checking status every 2s (attempt {pollAttempts}/30)…
            </span>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <label className="block text-sm">
            <span className="text-red-900 dark:text-red-200">
              Type the subdomain <code className="rounded bg-red-100 px-1 py-0.5">{subdomain}</code>{' '}
              to confirm:
            </span>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
              className="mt-1 w-full rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm focus:border-red-500 focus:outline-none"
            />
          </label>
          {error && (
            <div role="alert" className="rounded border border-red-400 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-red-800 dark:text-red-300">
              {error}
            </div>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={!matches || submitting}
            className="rounded bg-red-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
          >
            {submitting ? 'Queueing…' : 'Roll back tenant'}
          </button>
        </div>
      )}
    </section>
  );
}

// ---- SelfHostPanel (OCP-444) ----------------------------------

function SelfHostPanel({
  tenantId,
  subdomain,
  selfHostUrl,
}: {
  tenantId: string;
  subdomain: string;
  selfHostUrl: string | null;
}) {
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reveal, setReveal] = useState<SelfHostSecretReveal | null>(null);

  const rotate = useCallback(async () => {
    setRotating(true);
    setError(null);
    try {
      const r = await api<{
        beacon_secret: string;
        onboarding: { telemetry_endpoint: string; env: Record<string, string> };
        tenant: { subdomain: string; self_host_url: string | null };
      }>(`/v1/admin/tenants/${tenantId}/rotate-beacon-secret`, { method: 'POST' });
      setReveal({
        beaconSecret: r.beacon_secret,
        onboarding: r.onboarding,
        subdomain: r.tenant.subdomain,
        selfHostUrl: r.tenant.self_host_url,
        rotated: true,
      });
    } catch (err) {
      setError(err instanceof ApiError ? `Could not rotate secret (HTTP ${err.status}).` : 'Could not rotate secret.');
    } finally {
      setRotating(false);
    }
  }, [tenantId]);

  return (
    <section className="mt-10 rounded-lg border border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-900/20 p-5">
      <h2 className="text-base font-semibold text-sky-900 dark:text-sky-200">
        Self-host instance
      </h2>
      <p className="mt-2 text-sm text-sky-800 dark:text-sky-300">
        Telemetry-only tenant for an external instance we don&apos;t run - no Coolify service, no
        server, no billing. It authenticates its periodic beacon against the minted secret and its
        snapshots render below like any pool tenant.
      </p>
      <div className="mt-3 text-sm text-sky-900 dark:text-sky-200">
        Instance:{' '}
        {selfHostUrl ? (
          <a
            href={selfHostUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-brand-600 hover:text-brand-700 hover:underline"
          >
            {selfHostUrl}
          </a>
        ) : (
          <span className="font-mono">{subdomain}</span>
        )}
      </div>
      {error && (
        <div
          role="alert"
          className="mt-3 rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {error}
        </div>
      )}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => void rotate()}
          disabled={rotating}
          className="rounded border border-sky-300 dark:border-sky-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-900/40 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {rotating ? 'Rotating…' : 'Rotate beacon secret'}
        </button>
        <p className="mt-1 text-xs text-sky-700 dark:text-sky-400">
          Re-mints the secret and invalidates the old one immediately. Update the instance before
          its next beacon or telemetry pauses.
        </p>
      </div>
      {reveal && <SelfHostSecretModal reveal={reveal} onClose={() => setReveal(null)} />}
    </section>
  );
}

/**
 * OCP-368 - the declared team size drives the resource slice and the capacity
 * weight the placement picker counts. The buckets are the keys of the
 * `worker.team_size_capacity_weight` setting. Audited `tenant.team_size_update`.
 */
function TeamSizeEditor({ tenantId, profile, onChanged }: { tenantId: string; profile: ResourceProfile; onChanged: () => void }) {
  const [buckets, setBuckets] = useState<Record<string, number>>({});
  const [value, setValue] = useState(profile.team_size_bucket ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    api<{ setting?: { value?: Record<string, number> }; value?: Record<string, number> }>('/v1/admin/settings/worker.team_size_capacity_weight')
      .then((d) => setBuckets(d.setting?.value ?? d.value ?? {}))
      .catch(() => setBuckets({}));
  }, [open]);
  const save = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await api<{ team_size: string | null; capacity_weight: number }>(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/team-size`, {
        method: 'PATCH',
        body: { team_size: value === '' ? null : value, reason: reason.trim() },
      });
      setMessage(`Saved: team size ${res.team_size ?? 'unknown'}, capacity weight ${res.capacity_weight}. The resource slice follows on the next redeploy.`);
      setReason('');
      onChanged();
    } catch (err) {
      setMessage(err instanceof ApiError ? `Could not save (HTTP ${err.status}).` : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 rounded border border-gray-200 p-3 text-sm dark:border-gray-700" data-testid="team-size-editor">
      <div className="mb-2 text-gray-600 dark:text-gray-400">
        Declared team size <strong>{profile.team_size_bucket ?? 'unknown'}</strong>, capacity weight <strong>{profile.capacity_weight ?? 1}</strong> slot{(profile.capacity_weight ?? 1) === 1 ? '' : 's'} against the host&apos;s tenant limit.{' '}
        {!open && (
          <button type="button" className="text-brand-600 underline" onClick={() => setOpen(true)}>
            Change team size
          </button>
        )}
      </div>
      {open && (
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label="team size" className="rounded border border-gray-300 bg-white px-2 py-1 dark:border-gray-600 dark:bg-gray-900" value={value} onChange={(e) => setValue(e.target.value)}>
          <option value="">unknown (weight 1)</option>
          {Object.entries(buckets).map(([k, w]) => (
            <option key={k} value={k}>{k} (weight {w})</option>
          ))}
        </select>
        <input aria-label="reason" className="min-w-[16rem] flex-1 rounded border border-gray-300 bg-white px-2 py-1 dark:border-gray-600 dark:bg-gray-900" placeholder="Reason (audited)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <button type="button" className="rounded bg-brand-600 px-3 py-1 text-white disabled:opacity-50" disabled={busy || reason.trim().length < 3 || value === (profile.team_size_bucket ?? '')} onClick={() => void save()}>
          Save team size
        </button>
      </div>
      )}
      {message && <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{message}</p>}
    </div>
  );
}

function ResourceProfileSection({ profile }: { profile: ResourceProfile }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field
          label="Profile"
          value={profile.profile_name}
          hint={profile.team_size_bucket ? `team size ${profile.team_size_bucket}` : 'default'}
        />
        <Field label="Slice threads" value={String(profile.threads)} />
        <Field label="Slice RAM" value={`${profile.ram_mb} MB`} />
        <Field label="API workers" value={String(profile.api_workers)} />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="DB pool max" value={profile.env.DB_POOL_MAX} hint="per worker" />
        <Field label="max_connections" value={profile.env.POSTGRES_MAX_CONNECTIONS} />
        <Field label="shared_buffers" value={profile.env.PG_SHARED_BUFFERS} />
        <Field label="effective_cache_size" value={profile.env.PG_EFFECTIVE_CACHE_SIZE} />
        <Field label="work_mem" value={profile.env.PG_WORK_MEM} />
        <Field label="maintenance_work_mem" value={profile.env.PG_MAINTENANCE_WORK_MEM} />
        <Field label="random_page_cost" value={profile.env.PG_RANDOM_PAGE_COST} />
      </div>
    </div>
  );
}

/**
 * OCP-593 - the communication locale for operator mails about this tenant.
 * de + en have templates; any other value is stored and sent as en with the
 * fallback reported on every send. Audited `tenant.locale_update`.
 */
function LocaleField({ tenantId, locale, disabled, onSaved }: { tenantId: string; locale: string; disabled: boolean; onSaved: () => Promise<void> | void }) {
  const [value, setValue] = useState(locale);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => setValue(locale), [locale]);
  async function save(next: string) {
    setBusy(true);
    setNote(null);
    try {
      const r = await api<{ locale: string; template_available: boolean }>(`/v1/admin/tenants/${tenantId}/locale`, { method: 'PATCH', body: { locale: next } });
      setValue(r.locale);
      setNote(r.template_available ? null : `No mail template for "${r.locale}" - sends fall back to en and report it.`);
      await onSaved();
    } catch (err) {
      setNote(err instanceof ApiError ? `Could not save (HTTP ${err.status}).` : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Mail locale</div>
      <div className="mt-1 flex items-center gap-2 text-sm">
        <select
          value={value === 'de' || value === 'en' ? value : 'other'}
          onChange={(e) => { if (e.target.value !== 'other') void save(e.target.value); }}
          disabled={disabled || busy}
          data-testid="tenant-locale-select"
          className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-sm"
        >
          <option value="en">en</option>
          <option value="de">de</option>
          {value !== 'de' && value !== 'en' && <option value="other">{value} (no template)</option>}
        </select>
        <span className="text-xs text-gray-500">de | en have templates; other values via MCP</span>
      </div>
      {note && <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-300" data-testid="tenant-locale-note">{note}</div>}
    </div>
  );
}

/**
 * OCP-922 - change a scratch tenant's `scratch_expires_at` (the date it is
 * listed for hard-delete). Date + required reason; "No expiry" clears it.
 * Audited `tenant.scratch_expiry_changed`. Not shown for restore-drill scratch.
 */
function ScratchExpiryField({ tenantId, expiresAt, onSaved }: { tenantId: string; expiresAt: string | null; onSaved: () => Promise<void> | void }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(expiresAt ? expiresAt.slice(0, 10) : '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(expiresAt);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => { setCurrent(expiresAt); setDate(expiresAt ? expiresAt.slice(0, 10) : ''); }, [expiresAt]);
  async function save(next: string | null) {
    setBusy(true);
    setNote(null);
    try {
      const r = await api<{ scratch_expires_at: string | null }>(`/v1/admin/tenants/${encodeURIComponent(tenantId)}/scratch-expiry`, {
        method: 'PATCH',
        body: { scratch_expires_at: next, reason: reason.trim() },
      });
      setCurrent(r.scratch_expires_at);
      setNote(r.scratch_expires_at ? `Saved: scratch expiry ${r.scratch_expires_at.slice(0, 10)}.` : 'Saved: no scratch expiry.');
      setReason('');
      setOpen(false);
      await onSaved();
    } catch (err) {
      // The API's error code (not_scratch, restore_drill_scratch, tenant_purging ...) tells
      // the operator why the change was refused (OCP-922).
      const code = err instanceof ApiError ? (err.body as { error?: string } | null)?.error : undefined;
      setNote(err instanceof ApiError ? `Could not save (HTTP ${err.status}${code ? `, ${code}` : ''}).` : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }
  const reasonOk = reason.trim().length >= 10 && reason.trim().length <= 500;
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm" data-testid="scratch-expiry">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Scratch expiry</div>
      <div className="mt-1 text-sm" data-testid="scratch-expiry-value">
        {current ? current.slice(0, 10) : 'no expiry'}{' '}
        {!open && (
          <button type="button" className="text-brand-600 underline" onClick={() => setOpen(true)}>
            Change scratch expiry
          </button>
        )}
      </div>
      {open && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input type="date" aria-label="scratch expiry date" className="rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900" value={date} onChange={(e) => setDate(e.target.value)} />
          <input aria-label="scratch expiry reason" className="min-w-[14rem] flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-900" placeholder="Reason, 10 to 500 characters (audited)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <button type="button" className="rounded bg-brand-600 px-3 py-1 text-sm text-white disabled:opacity-50" disabled={busy || !reasonOk || date === ''} onClick={() => void save(`${date}T23:59:59Z`)}>
            Save scratch expiry
          </button>
          <button type="button" className="rounded border border-gray-300 px-3 py-1 text-sm disabled:opacity-50 dark:border-gray-600" disabled={busy || !reasonOk} onClick={() => void save(null)}>
            No expiry
          </button>
        </div>
      )}
      {note && <div className="mt-1 text-xs text-gray-600 dark:text-gray-400" data-testid="scratch-expiry-note">{note}</div>}
    </div>
  );
}

/**
 * OCP-643 - operator re-send (or rotate) of the first-boot setup link. The
 * instance login is a separate account from the portal; while the admin
 * password is pending, this link is the only way in. Same endpoint the MCP
 * tool ocp_send_tenant_setup_link uses, audited with source admin-ui.
 */
function SetupLinkField({ tenantId, onSent }: { tenantId: string; onSent: () => Promise<void> | void }) {
  const [rotate, setRotate] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function send() {
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      const r = await api<{ to: string; rotated: boolean; rotate_reason: string | null; message_id: string | null }>(
        `/v1/admin/tenants/${tenantId}/setup-link`,
        { method: 'POST', body: { rotate, ...(reason.trim() ? { reason: reason.trim() } : {}) } },
      );
      setNote(`Sent to ${r.to}${r.rotated ? ` - token rotated (${r.rotate_reason}), service redeploying` : ' - existing link re-sent'}${r.message_id ? `, message ${r.message_id}` : ''}.`);
      await onSent();
    } catch (err) {
      const code = err instanceof ApiError ? (err.body as { error?: string } | null)?.error : null;
      setError(
        code === 'password_already_set'
          ? 'The instance password is already set - the customer needs the instance\'s own password reset.'
          : code === 'encryption_unconfigured'
            ? 'ENCRYPTION_KEY is not configured on the api.'
            : code === 'deploy_in_flight'
              ? 'Another deploy of this tenant is in flight - the rotate redeploys, retry once it is done. Nothing was written or sent.'
              : `Could not send the setup link${code ? ` (${code})` : ''}.`,
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 shadow-sm" data-testid="setup-link-field">
      <div className="text-xs font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300">Setup link</div>
      <p className="mt-1 text-xs text-amber-900 dark:text-amber-200">
        The instance login is a separate account from the portal. Re-send the welcome link so the admin can set the instance password.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (audit)"
          className="w-40 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs"
        />
        <label className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
          <input type="checkbox" checked={rotate} onChange={(e) => setRotate(e.target.checked)} />
          rotate token (redeploys)
        </label>
        <button
          type="button"
          onClick={() => void send()}
          disabled={busy}
          className="rounded bg-amber-600 px-2 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
        >
          {busy ? 'Sending...' : 'Send setup link'}
        </button>
      </div>
      {note && <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">{note}</p>}
      {error && <p role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p>}
    </div>
  );
}

/** OCP-655 - one incident row as the admin surface lists it (voided rows stay, struck through). */
interface IncidentRow {
  id: string;
  opened_at: string;
  resolved_at: string | null;
  reason: string;
  minutes: number;
  state: 'open' | 'resolved' | 'voided';
  voided_by: string | null;
  void_reason: string | null;
}

/** OCP-630 - month availability from the minute probe, the same number the customer sees. */
function AvailabilityField({ tenantId }: { tenantId: string }) {
  const [a, setA] = useState<{
    month: string; closed: boolean; availability_pct: number; threshold_pct: number; below_threshold: boolean;
    minutes_counted_down: number; minutes_excluded: number; incidents: Array<{ opened_at: string; resolved_at: string | null; reason: string; minutes: number }>;
    incidents_voided?: number;
  } | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'unwired' | 'error'>('loading');
  // OCP-655 - the month's incidents incl. voided ones, with the void action.
  const [rows, setRows] = useState<IncidentRow[]>([]);
  const [voiding, setVoiding] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api<typeof a>(`/v1/admin/tenants/${tenantId}/availability`);
        if (cancelled) return;
        // A response without the number (older api, a proxy stub) is an error, not a crash.
        if (!r || typeof r.availability_pct !== 'number' || !Array.isArray(r.incidents)) {
          setState('error');
          return;
        }
        setA(r);
        setState('ok');
        try {
          const list = await api<{ items: IncidentRow[] }>(`/v1/admin/tenants/${tenantId}/reachability-incidents?month=${encodeURIComponent(r.month)}`);
          if (!cancelled && Array.isArray(list?.items)) setRows(list.items);
        } catch {
          // The list is a convenience on top of the number; a failure leaves the card usable.
        }
      } catch (err) {
        if (cancelled) return;
        setState(err instanceof ApiError && err.status === 503 ? 'unwired' : 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, reload]);
  const submitVoid = async (incidentId: string) => {
    setVoidError(null);
    try {
      await api(`/v1/admin/tenants/${tenantId}/reachability-incidents/${incidentId}/void`, { method: 'POST', body: { reason: voidReason.trim() } });
      setVoiding(null);
      setVoidReason('');
      setReload((n) => n + 1);
    } catch (err) {
      setVoidError(err instanceof ApiError ? `Could not void (HTTP ${err.status}).` : 'Could not void the incident.');
    }
  };
  const value =
    state === 'loading'
      ? '…'
      : state === 'unwired'
        ? 'probe not wired'
        : state === 'error' || !a
          ? 'could not load'
          : `${a.availability_pct.toFixed(3)} % (${a.month}${a.closed ? '' : ', so far'}; commitment ${a.threshold_pct} %${a.below_threshold ? ' - BELOW' : ''}; ${a.minutes_counted_down} min counted down${a.minutes_excluded ? `, ${a.minutes_excluded} min maintenance excluded` : ''}, ${a.incidents.length} incident${a.incidents.length === 1 ? '' : 's'})`;
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm" data-testid="availability-field">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Availability</div>
      <div className={`mt-1 text-sm font-medium ${a?.below_threshold ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'}`}>{value}</div>
      {rows.length > 0 ? (
        <ul className="mt-1 space-y-0.5 text-xs text-gray-600 dark:text-gray-400" data-testid="availability-incidents">
          {rows.slice(0, 8).map((i) => (
            <li key={i.id} data-testid={`incident-${i.state}`}>
              <span className={i.state === 'voided' ? 'line-through' : ''}>
                {fmtDate(i.opened_at)} {i.resolved_at ? `${i.minutes} min` : 'open'} ({i.reason})
              </span>
              {i.state === 'voided' ? (
                <span className="ml-1 text-gray-500" title={`voided by ${i.voided_by ?? '?'}`}>voided: {i.void_reason}</span>
              ) : voiding === i.id ? (
                <span className="ml-2 inline-flex items-center gap-1">
                  <input
                    type="text"
                    value={voidReason}
                    onChange={(e) => setVoidReason(e.target.value)}
                    placeholder="reason (min 10 chars), audited"
                    className="w-56 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-1 py-0.5 text-xs"
                    data-testid="void-reason"
                  />
                  <button type="button" onClick={() => void submitVoid(i.id)} disabled={voidReason.trim().length < 10} className="rounded bg-red-600 px-2 py-0.5 text-xs font-semibold text-white disabled:opacity-50" data-testid="void-confirm">
                    Void
                  </button>
                  <button type="button" onClick={() => { setVoiding(null); setVoidReason(''); }} className="text-xs underline">cancel</button>
                </span>
              ) : (
                <button type="button" onClick={() => { setVoiding(i.id); setVoidError(null); }} className="ml-2 text-xs underline" title="Exclude this incident from the SLA month as a probe-side false alarm (audited)" data-testid="void-start">
                  Void
                </button>
              )}
            </li>
          ))}
          {a && (a.incidents_voided ?? 0) > 0 && <li className="text-gray-500">{a.incidents_voided} voided this month (not counted)</li>}
        </ul>
      ) : (
        a && a.incidents.length > 0 && (
          <ul className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            {a.incidents.slice(0, 5).map((i) => (
              <li key={i.opened_at}>
                {fmtDate(i.opened_at)} {i.resolved_at ? `${i.minutes} min` : 'open'} ({i.reason})
              </li>
            ))}
          </ul>
        )
      )}
      {voidError && <div role="alert" className="mt-1 text-xs text-red-700 dark:text-red-300">{voidError}</div>}
    </div>
  );
}

function Field({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-gray-500">{hint}</div>}
    </div>
  );
}

function TelemetrySection({
  latest,
  recent,
}: {
  latest: TelemetryRow | null;
  recent: TelemetryRow[];
}) {
  if (!latest) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        No telemetry yet. Each tenant&apos;s beacon polls home every
        period_minutes; rows appear here once the first poll lands.
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Active users · 24h" value={fmt(latest.active_users_24h)} />
        <Kpi label="API requests" value={fmt(latest.api_requests_count)} />
        <Kpi label="AI calls" value={fmt(latest.ai_calls_count)} />
        <Kpi
          label="Storage · GB"
          value={latest.storage_total_gb === null ? '—' : latest.storage_total_gb.toFixed(2)}
        />
      </div>
      <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Snapshot</th>
              <th className="px-3 py-2 text-right">Users</th>
              <th className="px-3 py-2 text-right" title="Peak concurrent users">CCU peak</th>
              <th className="px-3 py-2 text-right">API req</th>
              <th className="px-3 py-2 text-right">AI calls</th>
              <th className="px-3 py-2 text-right">Storage GB</th>
              <th className="px-3 py-2 text-right">CPU %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {recent.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
                <td className="whitespace-nowrap px-3 py-2 tabular-nums text-gray-600 dark:text-gray-400">
                  {fmtDateTime(r.snapshot_at)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.active_users_24h)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.ccu_peak)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {fmt(r.api_requests_count)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{fmt(r.ai_calls_count)}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.storage_total_gb === null ? '—' : r.storage_total_gb.toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.cpu_pct_avg === null ? '—' : r.cpu_pct_avg.toFixed(1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">{value}</div>
    </div>
  );
}

function fmt(v: number | null): string {
  return v === null ? '—' : v.toLocaleString();
}
